import { useFeatureFlags } from "@hooks/http/use-feature-flag";
import { StepLink } from "@pages/onboarding/core/components/step-link";

import { EmailSupport } from "./components/email-support";
import { SetupSection } from "./components/setup-section";
import { OrderedList, SubSubSectionHeader } from "./components/styled";
import { SubmitTicketButton } from "./components/submit-ticket-button";
import { type PlatformDetails } from "./platform-details";

export const AttentiveDetails: PlatformDetails = {
  northbeamDocUrl:
    "https://docs.northbeam.io/docs/working-tracking-for-attentive-copy",
  officialDocUrl: "",
  additionalDetails: [
    {
      title: "How does the Attentive integration work?",
      content: (
        <>
          We connect directly to{" "}
          <a href="https://docs.attentive.com/pages/developer-guides/sftp-solution/attentive-sftp-data-feeds/">
            Attentive's data feed
          </a>
          , so you'll need an SFTP account
        </>
      ),
    },
    {
      title: "Why doesn’t the Attentive integration capture ad spend?",
      content: (
        <>
          Attentive’s integration does not provide spend data. Instead spend is
          tracked through Northbeam’s Spend API. You’ll need to{" "}
          <StepLink step="spend-api">configure the Spend API</StepLink> in next
          step.
        </>
      ),
    },
  ],
};

const SUPPORT_EMAIL_ADDRESS = "whiteglove@attentivemobile.com";

const EMAIL_TO_SUPPORT_MESSAGE = `Hello Attentive,

Northbeam is a multi-touch attribution system that helps us understand our customers' digital journey. We are currently working with them and would love to integrate Attentive Mobile reporting into our analytics within their platform.

We'd like to establish a SFTP data feed and would like daily logs for the following Attentive Mobile events:
JOIN
EMAIL_SAVE
IMPRESSION
CLICK
MESSAGE_RECEIPT
MESSAGE_LINK_CLICK
SUBSCRIPTION_OPTED_OUT
PURCHASE
Let me know if there's any more information you need for us!`;

const EMAIL_TO_SUPPORT_MESSAGE_HTML = (
  <>
    <p>Hello Attentive,</p>
    <p>
      Northbeam is a multi-touch attribution system that helps us understand our
      customers' digital journey. We are currently working with them and would
      love to integrate Attentive Mobile reporting into our analytics within
      their platform.
    </p>
    <div>
      We'd like to establish a SFTP data feed and would like daily logs for the
      following Attentive Mobile events:
      <ul className="ml-2 list-inside list-disc [&>li]:marker:text-xxs">
        <li>JOIN</li>
        <li>EMAIL_SAVE</li>
        <li>IMPRESSION</li>
        <li>CLICK</li>
        <li>MESSAGE_RECEIPT</li>
        <li>MESSAGE_LINK_CLICK</li>
        <li>SUBSCRIPTION_OPTED_OUT</li>
        <li>PURCHASE</li>
      </ul>
    </div>
    Let me know if there's any more information you need for us!
  </>
);

export const AttentiveSetup = ({
  connectionContent,
}: {
  connectionContent: React.ReactNode;
}) => {
  const { hasFeatureFlag } = useFeatureFlags();

  const displayAttentiveConnectionCard = hasFeatureFlag(
    "enableAttentiveConnection",
  );
  return (
    <>
      <SetupSection title="Create an Attentive SFTP account">
        <table className="table-fixed bg-white w-full rounded-lg border-collapse outline outline-1 outline-gray-1 text-xs leading-none">
          <thead>
            <tr>
              <th className="p-2 h-4 text-black/opacity-50 text-xs font-semibold border-b border-r">
                If you do not have an Attentive SFTP account
              </th>
              <th className="p-2 h-4 text-black/opacity-50 text-xs font-semibold border-b">
                If you’re not sure
              </th>
            </tr>
          </thead>
          <tbody className="bg-gray-4 border-t">
            <tr>
              <td rowSpan={2} className="p-2 border-r">
                <div className="text-xs leading-none">
                  <h1 className="heading mx-0 mb-0 mt-0 text-xs font-semibold leading-none">
                    Request an account:
                  </h1>
                  <div className="max-w-[480px] flex gap-2 justify-between items-center my-2">
                    Through your
                    <br />
                    ‘Attentive Client Strategy Representative’
                    <b>OR</b>
                    <div className="inline-flex flex-col gap-1">
                      White Glove team
                      <a href={`mailto:${SUPPORT_EMAIL_ADDRESS}`}>
                        {SUPPORT_EMAIL_ADDRESS}
                      </a>
                    </div>
                  </div>
                </div>
              </td>
              <td className="p-2 border-b">
                <div className="flex gap-2">
                  <span>1a.</span>
                  <span>
                    Log into your Attentive account and navigate to the
                    ’Settings’ tab
                  </span>
                </div>
              </td>
            </tr>
            <tr>
              <td className="p-2">
                <div className="flex gap-2">
                  <span>1b.</span>
                  <div>
                    <p className="m-0 mb-1 p-0">
                      Look for ’SFTP Account Management’
                    </p>
                    <p className="m-0 p-0">
                      If you do not see ’SFTP Account Management’, this means
                      you do not have an account
                    </p>
                  </div>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </SetupSection>
      <SetupSection title="Create Permanent Password">
        <div className="flex gap-2">
          <b>Note:</b>If you're creating a new account, you'll likely be issued
          a temporary password.
          <br />
          Please create a ’Permanent Password’ to ensure our connection doesn't
          break due to an expired password.
        </div>
        <div className="flex flex-col gap-2">
          <SubSubSectionHeader>
            Make sure you’ve followed these steps:
          </SubSubSectionHeader>
          <OrderedList variant="upper-alpha">
            <span>Log in to Attentive and navigate to the Settings tab.</span>
            <>
              Select ’SFTP Account Management’ from the menu.
              <OrderedList indent={1}>
                <span>
                  Log into your Attentive account and navigate to the ’Settings’
                  tab
                </span>
                <span>
                  If you don’t see this option, contact{" "}
                  <a href={`mailto:${SUPPORT_EMAIL_ADDRESS}`}>
                    {SUPPORT_EMAIL_ADDRESS}
                  </a>{" "}
                  or SFTP account.
                </span>
              </OrderedList>
            </>
          </OrderedList>
        </div>
      </SetupSection>
      <SetupSection title="Once your SFTP account is created, email Attentive to ensure daily logs are passed through your account">
        <EmailSupport
          emailAddress={SUPPORT_EMAIL_ADDRESS}
          emailAlternativeHint={
            <span className="text-gray+1">
              Or contact your ‘Client Strategy Representative’
            </span>
          }
          message={EMAIL_TO_SUPPORT_MESSAGE}
          messageContent={EMAIL_TO_SUPPORT_MESSAGE_HTML}
        />
      </SetupSection>
      {!displayAttentiveConnectionCard && (
        <SetupSection title="Submit ticket with your Attentive SFTP user name and password">
          <div>
            <SubmitTicketButton />
          </div>
        </SetupSection>
      )}
      {displayAttentiveConnectionCard && (
        <SetupSection title="Connect your Attentive SFTP">
          {connectionContent}
        </SetupSection>
      )}
    </>
  );
};
